import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import { serviceAreas, services } from './data';
import SunRaysHero from './SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

function ServicesHero() {
	return (
		<section className='relative isolate overflow-hidden border-b border-ink-100'>
			<SunRaysHero seed={1} />
			<div className='mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8'>
				<Eyebrow>Services</Eyebrow>
				<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05] max-w-3xl'>
					Solar built around{' '}
					<span className='italic text-primary-600'>you</span>.
				</h1>
				<p className='mt-6 max-w-2xl text-base leading-7 text-ink-600'>
					From your first conversation to your first kilowatt-hour, here is what
					working with Solar Gabe actually looks like.
				</p>
				<Link
					to='/contact'
					className='mt-8 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
				>
					Get a free quote
					<ArrowRightIcon className='h-4 w-4' />
				</Link>
			</div>
		</section>
	);
}

function ServiceCards() {
	return (
		<section className='bg-surface py-24 sm:py-32'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
					{services.map((s) => (
						<article
							id={s.shortId}
							key={s.id}
							className='group sg-card p-10'
						>
							<div className='flex items-center gap-5'>
								<div className='sg-badge h-12 w-12'>
									<s.icon
										className='h-6 w-6'
										aria-hidden='true'
									/>
								</div>
								<h2 className='font-display text-2xl font-medium text-ink-900'>
									{s.title}
								</h2>
							</div>
							<p className='mt-6 text-base leading-7 text-ink-500'>{s.body}</p>
							<ul className='mt-8 space-y-3'>
								{s.points.map((p) => (
									<li
										key={p}
										className='flex items-start gap-3'
									>
										<div className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100'>
											<CheckIcon
												className='h-3 w-3 text-primary-700'
												strokeWidth={3}
											/>
										</div>
										<span className='text-sm leading-6 text-ink-700'>{p}</span>
									</li>
								))}
							</ul>
							<div className='mt-10 pt-6 border-t border-ink-100'>
								<Link
									to={`/services/${s.id}`}
									className='inline-flex items-center gap-1 group-hover:gap-2 transition-all text-sm font-semibold text-primary-600'
								>
									Learn about {s.title.toLowerCase()}
									<ArrowRightIcon className='h-4 w-4' />
								</Link>
							</div>
						</article>
					))}
				</div>
			</div>
		</section>
	);
}

function ServiceAreasStrip() {
	return (
		<section className='bg-white border-y border-ink-100 py-20'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
					<div className='max-w-2xl'>
						<Eyebrow>Service areas</Eyebrow>
						<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
							Local solar guidance across the{' '}
							<span className='italic text-primary-600'>Mid-Atlantic</span>.
						</h2>
					</div>
					<Link
						to='/service-areas'
						className='inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700'
					>
						See all service areas
						<ArrowRightIcon className='h-4 w-4' />
					</Link>
				</div>
				<div className='mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
					{serviceAreas.map((area) => (
						<Link
							key={area.id}
							to={`/service-areas/${area.id}`}
							className='group sg-card sg-card-interactive p-6'
						>
							<h3 className='font-display text-xl font-medium text-ink-900'>
								{area.name}
							</h3>
							<p className='mt-3 text-sm leading-6 text-ink-500'>
								{area.summary}
							</p>
							<span className='mt-5 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all'>
								View area
								<ArrowRightIcon className='h-4 w-4' />
							</span>
						</Link>
					))}
				</div>
			</div>
		</section>
	);
}

function ProcessStrip() {
	const steps = [
		{ n: '01', title: 'Free assessment' },
		{ n: '02', title: 'Custom proposal' },
		{ n: '03', title: 'Permits + install' },
		{ n: '04', title: 'Power on + support' },
	];
	return (
		<section className='bg-white border-y border-ink-100 py-20'>
			<div className='mx-auto max-w-7xl px-6 lg:px-8'>
				<div className='flex flex-col items-center text-center'>
					<Eyebrow>How it works</Eyebrow>
					<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
						A clear path from question to{' '}
						<span className='italic text-primary-600'>kilowatt</span>.
					</h2>
				</div>
				<div className='mt-14 grid grid-cols-2 gap-px lg:grid-cols-4 bg-ink-100 rounded-2xl overflow-hidden ring-1 ring-ink-100'>
					{steps.map((s) => (
						<div
							key={s.n}
							className='bg-white p-8 text-center'
						>
							<div className='font-display text-4xl text-primary-600'>
								{s.n}
							</div>
							<div className='mt-3 text-sm font-medium text-ink-900'>
								{s.title}
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}

function ContactCta() {
	return (
		<section className='bg-surface py-24'>
			<div className='mx-auto max-w-3xl px-6 text-center lg:px-8'>
				<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
					Not sure which service fits?
				</h2>
				<p className='mt-4 text-base leading-7 text-ink-500'>
					Send me your latest electric bill. I&apos;ll come back with a clear
					recommendation — no commitment.
				</p>
				<Link
					to='/contact'
					className='mt-10 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
				>
					Start the conversation
					<ArrowRightIcon className='h-4 w-4' />
				</Link>
			</div>
		</section>
	);
}

export default function Services() {
	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<ServicesHero />
			<ServiceCards />
			<ServiceAreasStrip />
			<ProcessStrip />
			<ContactCta />
		</div>
	);
}
